function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState } from 'react';
import { bool, element, func, node, object, oneOf, string } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import SovosMenu from '../sovos-menu/SovosMenu';
import SovosMenuItem from '../sovos-menu-item/SovosMenuItem';
import SovosDivider from '../sovos-divider/SovosDivider';
import SovosIconButton from '../sovos-icon-button';
import { findChildrenByType } from '../internals/utils/reactHelpers';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const StyledIconButton = styled(SovosIconButton)({
  padding: 0
});

/**
 * Icon button that opens a menu. Most often used to show a "more" menu
 */

const SovosIconMenu = _ref => {
  let {
    children,
    classes,
    className,
    color,
    'data-testid': dataTestId,
    disabled = false,
    IconButtonProps = {},
    iconElement,
    MenuProps = {},
    onClose,
    onOpen,
    size,
    style,
    tooltipText
  } = _ref;
  const {
    t
  } = useMosaicTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const openMenu = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    if (onOpen) {
      onOpen();
    }
  };
  const onMenuClose = event => {
    event.stopPropagation();
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };
  const getHandleClick = onClick => event => {
    onMenuClose(event);
    if (onClick) {
      onClick();
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledIconButton, _extends({}, IconButtonProps, {
    "aria-expanded": open ? 'true' : undefined,
    "aria-haspopup": "true",
    classes: {
      root: classes === null || classes === void 0 ? void 0 : classes.root
    },
    className: clsx('sovosIconMenu', className),
    color: color,
    "data-testid": dataTestId,
    disabled: disabled,
    onClick: openMenu,
    size: size,
    style: style,
    tooltipText: tooltipText || t('open')
  }), iconElement), /*#__PURE__*/React.createElement(SovosMenu, _extends({}, MenuProps, {
    anchorEl: anchorEl,
    anchorOrigin: {
      horizontal: 'right',
      vertical: 'bottom'
    },
    className: "sovosIconMenu__menu",
    onClose: onMenuClose,
    open: open,
    transformOrigin: {
      horizontal: 'right',
      vertical: 'top'
    }
  }), findChildrenByType(children, SovosMenuItem, SovosDivider, Divider, MenuItem).map(menuItem => {
    const {
      props: menuItemProps
    } = menuItem;
    return menuItem.type === Divider || menuItem.type === SovosDivider ? menuItem : /*#__PURE__*/React.cloneElement(menuItem, {
      ...menuItemProps,
      className: clsx('sovosIconMenu__menuItem', menuItemProps.className),
      onClick: getHandleClick(menuItemProps.onClick)
    });
  })));
};
SovosIconMenu.propTypes = {
  /**
   * The content of the menu. Must be an array of `SovosMenuItem` or `SovosDivider`
   */
  children: node.isRequired,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Color of the icon button. Can be 'primary', 'secondary', or a valid
   * css color. Default is the text color.
   */
  color: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * If true, button is disabled
   */
  disabled: bool,
  /**
   * Object of props passed to the icon button
   */
  IconButtonProps: object,
  /**
   * An icon element for the icon button
   */
  iconElement: element.isRequired,
  /**
   * Object of props passed to the root of the menu
   */
  MenuProps: object,
  /**
   * Callback fired when menu is opened
   */
  onOpen: func,
  /**
   * Callback fired when menu is closed
   */
  onClose: func,
  /**
   * Size of the icon button
   */
  size: oneOf(['extraSmall', 'small', 'medium']),
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * Tooltip text for the icon button
   */
  tooltipText: string
};
export default SovosIconMenu;